import {
  CheckSquareOffset,
  Circle,
  Cube,
  PencilSimple,
  Receipt,
  XCircle,
} from '@phosphor-icons/react';
import { Flex, Spin, Typography, theme as antTheme } from 'antd';
import styled from 'styled-components';
import { ActionEditDots, ActionMenuItem, HeaderBackButton } from '../../../components';
import { ColoredPill } from '../../../elements';
import { OrderStatus, theme } from '../../../utility';

const { useToken } = antTheme;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;

function getStatusColor(status) {
  if (status === OrderStatus.shipped) return theme.colors.green;
  if (status === OrderStatus.canceled) return theme.colors.danger;
  if (status === OrderStatus.pendingFulfillment) return theme.colors.yellow;
  return theme.colors.grey;
}

/** AppHeader left row: back, order id, status, item count. */
export function OrderDetailHeaderLead({ data, loading }) {
  const { token } = useToken();
  const orderId = data?.marketplaceOrderDisplayId;
  const status = data?.states?.[0];
  const itemCount = data?.items?.length;

  return (
    <Flex
      align="center"
      gap={12}
      wrap="nowrap"
      style={{ minWidth: 0, maxWidth: '100%', height: '100%' }}
    >
      <HeaderBackButton />
      {loading ? (
        <Spin size="small" />
      ) : orderId || status || itemCount ? (
        <Flex align="center" gap={8} style={{ minWidth: 0 }} wrap="nowrap">
          {orderId ? (
            <Flex align="center" gap={6} style={{ minWidth: 0 }}>
              <IconWrap>
                <Cube size={18} color={token.colorText} />
              </IconWrap>
              <Typography.Text
                strong
                style={{
                  fontSize: 14,
                  lineHeight: 1,
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {orderId}
              </Typography.Text>
            </Flex>
          ) : null}

          {status ? (
            <ColoredPill mini color={getStatusColor(status)}>
              <Circle weight="fill" color={getStatusColor(status)} />
              {status}
            </ColoredPill>
          ) : null}

          {itemCount != null ? (
            <ColoredPill mini color={theme.colors.grey}>
              <Cube size={16} />
              {itemCount} Items
            </ColoredPill>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  );
}

/** AppHeader right-side actions for order detail (three-dot menu). */
export function OrderDetailHeaderActions({
  showAction,
  openPopup,
  cancelOrder,
  createLabel,
  createLabelDisabled = false,
  createLabelDisabledReason,
  openAddressPopup,
  data,
}) {
  const showCancelButton = data?.states?.includes?.(OrderStatus.pendingFulfillment);

  if (!showAction) return null;

  return (
    <ActionEditDots
      showByDefault
      inline
      basic
      preventChangingBg
      on="hover"
      position="bottom left"
    >
      <ActionMenuItem onClick={openPopup}>
        <CheckSquareOffset />
        Fulfill
      </ActionMenuItem>
      <ActionMenuItem
        onClick={createLabelDisabled ? undefined : createLabel}
        disabled={createLabelDisabled}
        title={createLabelDisabled ? createLabelDisabledReason : undefined}
      >
        <Receipt />
        Create Label
      </ActionMenuItem>
      <ActionMenuItem onClick={openAddressPopup}>
        <PencilSimple />
        Edit Address
      </ActionMenuItem>
      {showCancelButton ? (
        <ActionMenuItem onClick={cancelOrder} className="danger">
          <XCircle />
          Cancel Order
        </ActionMenuItem>
      ) : null}
    </ActionEditDots>
  );
}
